import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Check, ChevronDown, Coins, Swords, Trash2, X } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAcceptDuel, useDeclineDuel, useCancelDuel, useDeleteDuel, useMyDuels } from "@/modules/leagues/hooks/useDuels";
import { useLeagueParticipants } from "@/modules/leagues/hooks/useLeagueParticipants";
import { useMatch } from "@/modules/leagues/hooks/useMatches";
import { ScoreStepper } from "@/modules/leagues/components/ScoreStepper";
import { Button } from "@/components/atoms/Button";
import { Badge } from "@/components/atoms/Badge";
import { Skeleton } from "@/components/atoms/Skeleton";
import { EmptyState } from "@/components/organisms/EmptyState";
import { TrophyIllustration } from "@/components/atoms/illustrations";
import { useToast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";
import type { Duel } from "@/modules/leagues/types/duel";
import { cn } from "@/utils/cn";

const STATUS_LABEL: Record<Duel["status"], { label: string; variant: "default" | "success" | "warning" | "destructive" | "accent" }> = {
  pendente: { label: "Pendente", variant: "warning" },
  aceito: { label: "Em andamento", variant: "accent" },
  recusado: { label: "Recusado", variant: "destructive" },
  cancelado: { label: "Cancelado", variant: "default" },
  finalizado: { label: "Finalizado", variant: "success" },
  empate: { label: "Empate", variant: "default" },
  expirado: { label: "Expirado", variant: "default" },
};

function AcceptDuelForm({ duel, leagueId }: { duel: Duel; leagueId: string }) {
  const { toast } = useToast();
  const { data: match } = useMatch(leagueId, duel.matchId);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const acceptDuel = useAcceptDuel(leagueId);
  const declineDuel = useDeclineDuel(leagueId);

  return (
    <div className="mt-3 space-y-3 border-t border-border/60 pt-3">
      <p className="text-xs font-medium text-muted-foreground">Seu palpite pra esse duelo:</p>
      <div className="flex items-center justify-around">
        <ScoreStepper label={match?.homeTeam.shortName ?? "Casa"} value={homeScore} onChange={setHomeScore} />
        <ScoreStepper label={match?.awayTeam.shortName ?? "Fora"} value={awayScore} onChange={setAwayScore} />
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() =>
            declineDuel.mutate(duel.id, {
              onSuccess: () => toast({ title: "Desafio recusado" }),
            })
          }
          disabled={declineDuel.isPending || acceptDuel.isPending}
        >
          <X className="h-4 w-4" /> Recusar
        </Button>
        <Button
          className="flex-1"
          onClick={() =>
            acceptDuel.mutate(
              { duelId: duel.id, palpite: { homeScore, awayScore } },
              { onSuccess: () => toast({ title: "Desafio aceito! Boa sorte 🍀" }) }
            )
          }
          disabled={declineDuel.isPending || acceptDuel.isPending}
        >
          <Check className="h-4 w-4" /> Aceitar
        </Button>
      </div>
    </div>
  );
}

const DELETABLE_STATUSES: Duel["status"][] = ["finalizado", "empate"];

function DuelCard({ duel, myUid, leagueId }: { duel: Duel; myUid: string; leagueId: string }) {
  const { toast } = useToast();
  const cancelDuel = useCancelDuel(leagueId);
  const deleteDuel = useDeleteDuel(leagueId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isChallenger = duel.desafianteUid === myUid;
  // Nome ATUAL do participante, não o que foi salvo no momento em que o
  // duelo foi criado — sem isto, trocar o nome no Perfil nunca atualizava
  // quem aparece nos duelos já existentes (bug relatado: nome antigo
  // continuava aparecendo no X1 mesmo depois de mudar em Perfil). Cai de
  // volta pro nome salvo se a pessoa não for mais encontrada como
  // participante (ex: saiu da liga) — nunca mostra vazio.
  const { data: participants } = useLeagueParticipants(leagueId);
  const currentNameByUid = new Map((participants ?? []).map((p) => [p.uid, p.nome]));
  const currentDesafianteNome = currentNameByUid.get(duel.desafianteUid) ?? duel.desafianteNome;
  const currentDesafiadoNome = currentNameByUid.get(duel.desafiadoUid) ?? duel.desafiadoNome;
  const opponentName = isChallenger ? currentDesafiadoNome : currentDesafianteNome;
  const iWon = duel.vencedorUid === myUid;
  const needsMyResponse = duel.status === "pendente" && !isChallenger;
  const isFinal = DELETABLE_STATUSES.includes(duel.status);

  // A PARTIDA é a base do duelo — precisa continuar visível em toda etapa
  // (pendente, aceito, finalizado), não só na hora de responder. Busca
  // pelo MESMO matchId já salvo desde a criação (nunca duplicamos dado de
  // partida dentro do duelo, só a referência) — funciona igual pra
  // duelos antigos, já que o matchId sempre existiu.
  const { data: match } = useMatch(leagueId, duel.matchId);
  const isMatchLive = match?.status === "live";
  const isMatchFinished = match?.status === "finished";

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <Swords className="h-3.5 w-3.5 text-muted-foreground" />
            {isChallenger ? `Você desafiou ${opponentName}` : `${opponentName} te desafiou`}
          </p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <Coins className="h-3 w-3" /> R$ {duel.valorReais.toFixed(2)} · Rodada {duel.round}
          </p>
        </div>
        <Badge variant={STATUS_LABEL[duel.status].variant}>{STATUS_LABEL[duel.status].label}</Badge>
      </div>

      {/* PARTIDA — times, escudos, e o placar REAL (ao vivo ou final).
          Sempre visível, nunca some depois que o desafio é aceito. Nunca
          confundir com os palpites dos usuários, mostrados separado
          abaixo. */}
      {match && (
        <div className="mt-3 rounded-xl border border-border/60 bg-muted/40 px-3 py-2.5">
          <p className="flex items-center justify-center gap-2 text-center text-sm font-semibold">
            {match.homeTeam.crestUrl && <img src={match.homeTeam.crestUrl} alt="" className="h-5 w-5 shrink-0" />}
            <span>{match.homeTeam.name}</span>
            {isMatchLive || isMatchFinished ? (
              <span className="font-display tabular-nums text-foreground">
                {match.homeScore} × {match.awayScore}
              </span>
            ) : (
              <span className="text-muted-foreground">×</span>
            )}
            <span>{match.awayTeam.name}</span>
            {match.awayTeam.crestUrl && <img src={match.awayTeam.crestUrl} alt="" className="h-5 w-5 shrink-0" />}
          </p>
          {(isMatchLive || isMatchFinished) && (
            <p
              className={cn(
                "mt-1 text-center text-[10px] font-bold uppercase tracking-wide",
                isMatchLive ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {isMatchLive ? "● Ao vivo" : "Placar final"}
            </p>
          )}
        </div>
      )}

      {(duel.status === "aceito" || duel.status === "finalizado" || duel.status === "empate") && (
        <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border/60 pt-3 text-center text-sm">
          <div>
            <p className="text-[10px] uppercase text-muted-foreground">{isChallenger ? "Seu palpite" : currentDesafianteNome}</p>
            <p className="font-display font-bold tabular-nums">
              {duel.desafiantePalpite.homeScore} × {duel.desafiantePalpite.awayScore}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-muted-foreground">{!isChallenger ? "Seu palpite" : currentDesafiadoNome}</p>
            <p className="font-display font-bold tabular-nums">
              {duel.desafiadoPalpite?.homeScore} × {duel.desafiadoPalpite?.awayScore}
            </p>
          </div>
        </div>
      )}

      {duel.status === "finalizado" && (
        <p className={cn("mt-2 text-center text-sm font-bold", iWon ? "text-success" : "text-muted-foreground")}>
          {iWon ? "Você ganhou o duelo! 🏆" : `${opponentName} ganhou o duelo.`}
        </p>
      )}
      {duel.status === "empate" && <p className="mt-2 text-center text-sm font-bold text-muted-foreground">Duelo empatado — ninguém deve nada.</p>}
      {duel.status === "expirado" && (
        <p className="mt-2 text-center text-sm text-muted-foreground">
          {isChallenger
            ? `${opponentName} não respondeu a tempo — o duelo expirou quando a partida começou.`
            : "Esse duelo expirou porque a partida começou antes de você responder."}
        </p>
      )}

      {needsMyResponse && <AcceptDuelForm duel={duel} leagueId={leagueId} />}

      {duel.status === "pendente" && isChallenger && (
        <Button
          variant="outline"
          size="sm"
          className="mt-3 w-full"
          onClick={() => cancelDuel.mutate(duel.id, { onSuccess: () => toast({ title: "Desafio cancelado" }) })}
          disabled={cancelDuel.isPending}
        >
          Cancelar desafio
        </Button>
      )}

      {isFinal && (
        <div className="mt-3 border-t border-border/60 pt-3">
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              aria-label="Excluir duelo"
              className="flex w-full items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" /> Excluir
            </button>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <span className="text-xs text-muted-foreground">Excluir esse duelo?</span>
              <button
                onClick={() => setConfirmDelete(false)}
                className="rounded-lg px-2 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted"
              >
                Não
              </button>
              <button
                onClick={() =>
                  deleteDuel.mutate(duel.id, {
                    onSuccess: () => toast({ title: "Duelo excluído" }),
                    onError: (err) =>
                      toast({
                        title: "Não foi possível excluir",
                        description: err instanceof Error ? err.message : "Tente novamente.",
                        variant: "destructive",
                      }),
                  })
                }
                disabled={deleteDuel.isPending}
                className="rounded-lg bg-destructive px-2 py-1 text-xs font-semibold text-destructive-foreground hover:brightness-95"
              >
                {deleteDuel.isPending ? "Excluindo..." : "Sim, excluir"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DuelsPage() {
  const { league } = useLeagueContext();
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  const { data: duels, isLoading } = useMyDuels(league.id);
  const [showRules, setShowRules] = useState(false);

  const myUid = firebaseUser?.uid ?? "";

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(ROUTES.leaguePredictions(league.id))}
          aria-label="Voltar"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="font-display text-base font-bold">Duelos 1x1</h1>
      </div>

      <div className="rounded-2xl border border-border bg-card">
        <button
          onClick={() => setShowRules((v) => !v)}
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
        >
          <span className="flex items-center gap-2 text-sm font-semibold">
            <BookOpen className="h-4 w-4 text-primary" /> Regras do Desafio X1
          </span>
          <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", showRules && "rotate-180")} />
        </button>
        {showRules && (
          <div className="space-y-3 border-t border-border px-4 py-3 text-xs text-muted-foreground">
            <p>
              Você desafia outro participante escolhendo uma partida, seu palpite e o valor combinado. O desafiado
              tem até o horário oficial de início da partida pra aceitar — depois disso o desafio expira sozinho.
            </p>
            <div>
              <p className="mb-1 font-semibold text-foreground">Como o vencedor é decidido, em ordem:</p>
              <ol className="list-decimal space-y-1 pl-4">
                <li>Quem cravar o placar exato vence na hora.</li>
                <li>Se ninguém cravar, quem acertou o resultado (vitória de qual time, ou empate) vence.</li>
                <li>Se os dois acertaram o resultado, vence quem chegou mais perto do número de gols do time vencedor.</li>
                <li>Se ainda empatados nisso, vence quem chegou mais perto do número de gols do time perdedor.</li>
                <li>Se continuar empatado em tudo, o duelo é declarado empate — ninguém deve nada a ninguém.</li>
              </ol>
            </div>
            <p>
              Quando a própria partida termina empatada, o critério muda um pouco: placar exato primeiro, depois quem
              acertou que seria empate, depois quem chegou mais perto da quantidade de gols, e por último empate.
            </p>
            <p className="rounded-lg bg-muted px-3 py-2">
              💰 O app <strong>nunca processa pagamento</strong> — só registra o valor combinado e quem venceu. O
              acerto é feito entre vocês, fora do aplicativo.
            </p>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      )}

      {!isLoading && (!duels || duels.length === 0) && (
        <EmptyState
          illustration={TrophyIllustration}
          title="Nenhum duelo ainda"
          description="Escolha uma partida na aba Palpites e toque em 'Desafiar' pra criar seu primeiro duelo 1x1."
        />
      )}

      {duels && duels.length > 0 && (
        <div className="space-y-3">
          {duels.map((d) => (
            <DuelCard key={d.id} duel={d} myUid={myUid} leagueId={league.id} />
          ))}
        </div>
      )}
    </div>
  );
}
